/**
 * CallStatusUI
 * Two small pieces of persistent call UI that sit outside the main layout:
 *
 *   MicErrorBanner  — shown when the browser denied mic access
 *   DNDToggle       — floating availability toggle (available / do-not-disturb)
 */

import { MicOff, X, BellOff, Bell } from '@/shared/ui/icons';
import { useCall } from './CallContext';

// ─── Mic error banner ─────────────────────────────────────────────────────────

export function MicErrorBanner() {
  const { micError, clearMicError } = useCall();
  if (!micError) return null;

  const message =
    micError === 'denied'
      ? 'Microphone access was denied. Open your browser settings and allow microphone access to make calls.'
      : 'No microphone found. Plug in a microphone to make calls.';

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[300] flex items-start gap-3 px-4 py-3 rounded-xl border border-destructive/30 bg-destructive/10 shadow-lg max-w-sm w-[calc(100%-2rem)] pointer-events-auto"
    >
      <MicOff className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
      <p
        className="flex-1 font-apple text-foreground leading-snug"
        style={{ fontSize: 'var(--text-label)' }}
      >
        {message}
      </p>
      <button
        type="button"
        onClick={clearMicError}
        className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── DND toggle ───────────────────────────────────────────────────────────────

export function DNDToggle() {
  const { availability, setAvailability, status } = useCall();
  // Only show when idle — no point switching DND mid-call
  if (status !== 'idle') return null;

  const isDND = availability === 'dnd';

  return (
    <button
      type="button"
      onClick={() => setAvailability(isDND ? 'available' : 'dnd')}
      title={isDND ? 'Do Not Disturb — click to go available' : 'Available — click to enable Do Not Disturb'}
      className="fixed bottom-6 right-6 z-[140] flex items-center gap-2 px-3 py-2 rounded-xl border bg-card shadow-md transition-all cursor-pointer hover:shadow-lg pointer-events-auto"
      style={{
        borderColor: isDND ? 'var(--destructive)' : 'var(--border)',
        fontSize: 'var(--text-search)',
      }}
    >
      {isDND
        ? <BellOff className="w-3.5 h-3.5 text-destructive" />
        : <Bell    className="w-3.5 h-3.5 text-muted-foreground" />
      }
      <span className={`font-apple font-medium ${isDND ? 'text-destructive' : 'text-muted-foreground'}`}>
        {isDND ? 'DND' : 'Available'}
      </span>
    </button>
  );
}
